import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { searchByUsername } from '../services/api'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { setCredentials, updateUser } from '../store/slices/authSlice'
import { authService } from '../services/authService'
import { can } from '../utils/permissions'
import UserDetailModal from './UserDetailModal'
// import IntegrityAlertsBell from './IntegrityAlertsBell' // System Tracker bell hidden
import OwnerDepositModal from './OwnerDepositModal'

import logo from '../assets/main/logo.d99exch.png'

const useViewport = import.meta.env.VITE_USE_VIEWPORT !== 'false'

function Header() {
  const { user, token, role, isAuthenticated } = useSelector((state) => state.auth)
  const { logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  // One menu at a time: opening a dropdown closes whichever one was open.
  // 'liveMarket' | 'liveVirtualMarket' | 'reports' | 'settlement' | 'user' | null
  const [openMenu, setOpenMenu] = useState(null)
  const toggleMenu = (name) => setOpenMenu((current) => (current === name ? null : name))
  // Menus also open on plain hover (CSS). Hovering a DIFFERENT dropdown while one
  // is click-opened must close that one, or two menus end up open side by side.
  const closeOtherMenuOnHover = (name) => (e) => {
    const li = e.currentTarget
    // A click-opened menu is sized by the layout effect below; this is the hover-opened one.
    if (li.matches('.main-menu .nav-item.dropdown:not(.show)')) fitMenuToWindow(li)
    setOpenMenu((current) => (current && current !== name ? null : current))
  }
  // A long menu (Live Market: 28 items) is taller than a laptop window, and the header is sticky, so
  // scrolling the page never reaches its last items. Cap the menu to the room under its top edge
  // (header.css makes it scroll); offsetY = how far the menu is shifted from the toggle's bottom.
  const fitMenuToWindow = (li, offsetY = 0) => {
    const menu = li.querySelector(':scope > .dropdown-menu')
    if (!menu) return
    const top = li.getBoundingClientRect().bottom + offsetY
    menu.style.maxHeight = `${Math.max(120, Math.floor(window.innerHeight - top - 5))}px`
  }
  // Reference (bootstrap-vue + Popper) placement of an open header menu: under the toggle, or, when it
  // doesn't fit below it, flipped and clamped 5px from the viewport top — i.e. round(5 - toggle.top).
  useLayoutEffect(() => {
    document.querySelectorAll('#page-topbar .main-menu .nav-item.dropdown').forEach((li) => {
      const menu = li.querySelector(':scope > .dropdown-menu')
      if (!menu) return
      if (!li.classList.contains('show')) {
        menu.style.removeProperty('--popper-y')
        fitMenuToWindow(li) // may still be showing on hover, now back under the toggle
        return
      }
      const r = li.getBoundingClientRect()
      // scrollHeight = the menu's full height, also when a max-height already caps it
      const fitsBelow = r.bottom + menu.scrollHeight <= window.innerHeight
      const popperY = fitsBelow ? 0 : Math.round(5 - r.top)
      menu.style.setProperty('--popper-y', `${popperY}px`)
      fitMenuToWindow(li, popperY)
    })
  }, [openMenu])
  const liveMarketOpen = openMenu === 'liveMarket'
  const liveVirtualMarketOpen = openMenu === 'liveVirtualMarket'
  const reportsOpen = openMenu === 'reports'
  const settlementOpen = openMenu === 'settlement'
  const userMenuOpen = openMenu === 'user'
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [depositModalOpen, setDepositModalOpen] = useState(false)

  const searchRef = useRef(null)
  const searchTimerRef = useRef(null)

  // Fetch user profile if authenticated but user data is missing
  useEffect(() => {

    const fetchUserProfile = async () => {
      // Executives have no profile endpoint — their user (with permissions) is
      // restored from localStorage by the auth slice. Skip the fetch.
      if (role === 'executive') return
      // Only fetch if we have a token but no user data
      if (isAuthenticated && token && !user) {
        try {
          console.log('Header: Fetching user profile...')
          // Same pair as the login flow: owner -> /admin/owner/profile,
          // staff -> /admin/staff/profile, both { data: { user } }.
          const profileResponse = role === 'owner'
            ? await authService.getOwnerProfileV2()
            : await authService.getProfile()

          console.log('Header: Profile response:', profileResponse)

          const userData = profileResponse.data?.user

          if (profileResponse && profileResponse.success && userData) {
            dispatch(setCredentials({
              token: token,
              user: userData,
              role: role
            }))
            console.log('Header: Profile loaded successfully')
          } else {
            console.warn('Header: Profile response missing user data:', profileResponse)
          }
        } catch (error) {
          console.error('Header: Failed to fetch profile:', error)
          // Don't logout on error, just log it
        }
      }
    }


 fetchUserProfile();

  // 🧹 Cleanup on unmount / dependency change

  }, [isAuthenticated, token, user, role, dispatch])

  // Owner self-deposit ke baad: page reload nahi — sirf profile dobara padho aur
  // redux user update kar do, taaki header/dashboard ke numbers turant naye dikhein.
  const refreshOwnerProfile = async () => {
    try {
      const res = await authService.getOwnerProfileV2()
      const userData = res?.data?.user
      if (res?.success && userData) dispatch(updateUser(userData))
      // Jo pages apna profile khud rakhte hain (users2 ka Master Balance banner)
      // unhe bata do ki paisa hil gaya hai — wo apna data dobara le lenge.
      window.dispatchEvent(new Event('owner:balance-updated'))
    } catch (error) {
      console.error('Header: Failed to refresh owner profile:', error)
    }
  }

  // Close the open menu / search when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      // A press on another dropdown is left to its toggle, which swaps the menu
      if (!event.target.closest?.('.dropdown')) {
        setOpenMenu(null)
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false)
        setSearchValue('')
      }
    }

    if (openMenu || searchOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMenu, searchOpen])

  // Picking a menu item navigates — the menu should not stay open over the new page
  useEffect(() => {
    setOpenMenu(null)
  }, [location.pathname])

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchValue(value)
    // Show dropdown when user types
    if (value.trim()) {
      setSearchOpen(true)
      // Debounce network calls and use shared service
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      searchTimerRef.current = setTimeout(async () => {
        try {
          const query = value.trim()
          const res = await searchByUsername(query, 1, 10)
          // service returns { success: true, data: { results: [...] } }
          const results = res?.data?.results || []
          setSearchResults(results)
        } catch (err) {
          console.error('Header: search API failed', err)
          setSearchResults([])
        }
      }, 300)
    } else {
      // clear debounce and results
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
        searchTimerRef.current = null
      }
      setSearchResults([])
    }
  }
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
        searchTimerRef.current = null
      }
    }
  }, [])

  const handleSearchClick = () => {
    setSearchOpen(true)
  }

  // Toggle sidebar - when vertical-collpsed class is present, sidebar is hidden
  const handleSidebarToggle = () => {
    const body = document.body
    body.classList.toggle('vertical-collpsed')
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <header id="page-topbar" className={!useViewport ? 'header-desktop-on-mobile' : ''}>
      <div className="navbar-header">
        <div className="d-flex">
          {/* Logo */}
          <div className="navbar-brand-box">
            <Link to="/admin/users2" className="logo logo-light">
              <span className="logo-lg">
                <img src={logo} alt="" className="site-logo" />
              </span>
            </Link>
          </div>

          {/* Hamburger Menu Button */}
          <button
            id="vertical-menu-btn"
            type="button"
            className="btn btn-sm px-3 font-size-16 header-item"
            onClick={handleSidebarToggle}
          >
            <i className="fa fa-fw fa-bars"></i>
          </button>

          {/* Main Navigation Menu */}
          <ul className="nav align-items-center main-menu">
            {can('userList') && (
            <li className="nav-item">
              <Link to="/admin/users" className="nav-link">
                List of Clients
              </Link>
            </li>
            )}

            {can('agentAssign') && (
            <li className="nav-item">
              <Link to="/admin/assign-agent" className="nav-link">
                Assign Agent
              </Link>
            </li>
            )}

            {can('marketAnalysis') && (
            <li className="nav-item">
              <Link to="/admin/market-analysis" className="nav-link">
                Market Analysis
              </Link>
            </li>
            )}

            {/* System Tracker menu — hidden. Wapas dikhana ho to ye block uncomment karo.
            {role === 'owner' && (
            <li className="nav-item">
              <Link to="/admin/system-tracker" className="nav-link">
                System Tracker
              </Link>
            </li>
            )} */}

            {/* Live Market Dropdown */}
            {can('ourCasino') && (
            <li className={`nav-item dropdown ${liveMarketOpen ? 'show' : ''}`} onMouseEnter={closeOtherMenuOnHover('liveMarket')}>
              <a
                href="#"
                className="nav-link dropdown-toggle"
                onClick={(e) => {
                  e.preventDefault()
                  toggleMenu('liveMarket')
                }}
                role="button"
                aria-expanded={liveMarketOpen}
              >
                Live Market <i className="fa fa-caret-down" />
              </a>
              <div className={`dropdown-menu ${liveMarketOpen ? 'show' : ''}`}>
                {/* Items and order exactly as the reference admin's Live Market menu */}
                <Link to="/admin/casino/premium-casino" className="dropdown-item">
                  <span>Premium Casino</span>
                </Link>
                <Link to="/admin/casino/tembo-casino" className="dropdown-item">
                  <span>Tembo Casino</span>
                </Link>
                <Link to="/admin/casino/vip" className="dropdown-item">
                  <span>Vip Casino</span>
                </Link>
                <Link to="/admin/casino/lucky5" className="dropdown-item">
                  <span>Lucky 6</span>
                </Link>
                <Link to="/admin/casino/mogambo" className="dropdown-item">
                  <span>Mogambo</span>
                </Link>
                <Link to="/admin/casino/teenunique" className="dropdown-item">
                  <span>Unique Teenpatti</span>
                </Link>
                <Link to="/admin/casino/roulettelist" className="dropdown-item">
                  <span>Roulette</span>
                </Link>
                <Link to="/admin/casino/superover2" className="dropdown-item">
                  <span>Super Over2</span>
                </Link>
                <Link to="/admin/casino/lucky15" className="dropdown-item">
                  <span>Lucky15</span>
                </Link>
                <Link to="/admin/casino/goal" className="dropdown-item">
                  <span>Goal</span>
                </Link>
                <Link to="/admin/casino/goal2" className="dropdown-item">
                  <span>Goal2</span>
                </Link>
                <Link to="/admin/market-analysis" className="dropdown-item">
                  <span>Binary</span>
                </Link>
                <Link to="/admin/casino/race20" className="dropdown-item">
                  <span>Race 20-20</span>
                </Link>
                <Link to="/admin/casino/queen" className="dropdown-item">
                  <span>Queen</span>
                </Link>
                <Link to="/admin/casino/baccaratlist" className="dropdown-item">
                  <span>Baccarat</span>
                </Link>
                <Link to="/admin/casino/sportscasinolist" className="dropdown-item">
                  <span>Sport Casino</span>
                </Link>
                <Link to="/admin/casino/war" className="dropdown-item">
                  <span>Casino War</span>
                </Link>
                <Link to="/admin/casino/worlilist" className="dropdown-item">
                  <span>Worli</span>
                </Link>
                <Link to="/admin/casino/3cardj" className="dropdown-item">
                  <span>3 Card Judgement</span>
                </Link>
                <Link to="/admin/casino/cards32list" className="dropdown-item">
                  <span>32 Card Casino</span>
                </Link>
                <Link to="/admin/casino/liveteenpattilist" className="dropdown-item">
                  <span>Live Teenpatti</span>
                </Link>
                <Link to="/admin/casino/teenpatti2list" className="dropdown-item">
                  <span>Teenpatti 2.0</span>
                </Link>
                <Link to="/admin/casino/livepokerlist" className="dropdown-item">
                  <span>Live Poker</span>
                </Link>
                <Link to="/admin/casino/andarbaharlist" className="dropdown-item">
                  <span>Andar Bahar</span>
                </Link>
                <Link to="/admin/casino/lucky7list" className="dropdown-item">
                  <span>Lucky 7</span>
                </Link>
                <Link to="/admin/casino/dragontigerlist" className="dropdown-item">
                  <span>Dragon Tiger</span>
                </Link>
                <Link to="/admin/casino/bollywood-casino" className="dropdown-item">
                  <span>Bollywood Casino</span>
                </Link>
                <Link to="/admin/casino/other-casino" className="dropdown-item">
                  <span>Others</span>
                </Link>
              </div>
            </li>
            )}

            {/* Live Virtual Market Dropdown */}
            {can('ourCasino') && (
            <li className={`nav-item dropdown ${liveVirtualMarketOpen ? 'show' : ''}`} onMouseEnter={closeOtherMenuOnHover('liveVirtualMarket')}>
              <a
                href="#"
                className="nav-link dropdown-toggle"
                onClick={(e) => {
                  e.preventDefault()
                  toggleMenu('liveVirtualMarket')
                }}
                role="button"
                aria-expanded={liveVirtualMarketOpen}
              >
                Live Virtual Market <i className="fa fa-caret-down" />
              </a>
              <div className={`dropdown-menu ${liveVirtualMarketOpen ? 'show' : ''}`}>
                <Link to="/admin/vcasino/dtl20" className="dropdown-item">
                  <span>20-20 DTL</span>
                </Link>
                <Link to="/admin/vcasino/aaa" className="dropdown-item">
                  <span>Amar Akbar Anthony</span>
                </Link>
                <Link to="/admin/vcasino/teenmuf" className="dropdown-item">
                  <span>Muflis Teenpatti</span>
                </Link>
                <Link to="/admin/vcasino/teen" className="dropdown-item">
                  <span>1 Day Teenpatti</span>
                </Link>
                <Link to="/admin/vcasino/dt6" className="dropdown-item">
                  <span>1 Day Dragon Tiger</span>
                </Link>
                <Link to="/admin/vcasino/lucky7" className="dropdown-item">
                  <span>Lucky 7</span>
                </Link>
                <Link to="/admin/vcasino/btable" className="dropdown-item">
                  <span>Bollywood Casino</span>
                </Link>
                <Link to="/admin/vcasino/teen20" className="dropdown-item">
                  <span>20-20 Teenpatti</span>
                </Link>
                <Link to="/admin/vcasino/trio" className="dropdown-item">
                  <span>Trio</span>
                </Link>
              </div>
            </li>
            )}

            {/* Reports Dropdown — shown if the executive holds any report perm */}
            {can(['accountStatement','currentBets','partyWinLoss','events','liveCasinoResult','casinoResult','generalLock','userLock','userList']) && (
            <li className={`nav-item dropdown ${reportsOpen ? 'show' : ''}`} onMouseEnter={closeOtherMenuOnHover('reports')}>
              <a
                href="#"
                className="nav-link dropdown-toggle"
                onClick={(e) => {
                  e.preventDefault()
                  toggleMenu('reports')
                }}
                role="button"
                aria-expanded={reportsOpen}
              >
                Reports <i className="fa fa-caret-down" />
              </a>
              <div className={`dropdown-menu ${reportsOpen ? 'show' : ''}`}>
                {can('accountStatement') && (
                <Link to="/admin/reports/accountstatement" className="side-nav-link-ref dropdown-item">
                  Account Statement
                </Link>
                )}
                {can('currentBets') && (
                <Link to="/admin/reports/currentbets" className="side-nav-link-ref dropdown-item">
                  Current Bets
                </Link>
                )}
                {can('partyWinLoss') && (
                <Link to="/admin/reports/generalreport" className="side-nav-link-ref dropdown-item">
                  General Report
                </Link>
                )}
                {can('events') && (
                <Link to="/admin/reports/gamereport" className="side-nav-link-ref dropdown-item">
                  Game Report
                </Link>
                )}
                {can('liveCasinoResult') && (
                <Link to="/admin/reports/livecasinoreport" className="side-nav-link-ref dropdown-item">
                  Casino Report
                </Link>
                )}
                {can('partyWinLoss') && (
                <Link to="/admin/reports/profitloss" className="side-nav-link-ref dropdown-item">
                  Profit And Loss
                </Link>
                )}
                {can('casinoResult') && (
                <Link to="/admin/reports/casinoresult" className="side-nav-link-ref dropdown-item">
                  Casino Result Report
                </Link>
                )}
                {can('userLock') && (
                <Link to="/admin/settings/userlock" className="side-nav-link-ref dropdown-item">
                  General Lock
                </Link>
                )}
                {/* Global Settings — sirf owner ko dikhta hai */}
                {role === 'owner' && (
                <Link to="/admin/settings/global-settings" className="side-nav-link-ref dropdown-item">
                  Global Settings
                </Link>
                )}
                {/* Commission — sirf owner: user ki jeet par market-wise % (commission-flow.md) */}
                {role === 'owner' && (
                <Link to="/admin/settings/commission" className="side-nav-link-ref dropdown-item">
                  Commission
                </Link>
                )}
                {/* Auto Void — sirf owner: declared event ki bina-result bets ka timer + tracker */}
                {role === 'owner' && (
                <Link to="/admin/settings/auto-void" className="side-nav-link-ref dropdown-item">
                  Auto Void
                </Link>
                )}
                {can('partyWinLoss') && (
                <Link to="/admin/reports/totalprofitloss" className="side-nav-link-ref dropdown-item">
                  Total Profit Loss
                </Link>
                )}
                {can('partyWinLoss') && (
                <Link to="/admin/reports/userwinloss" className="side-nav-link-ref dropdown-item">
                  User Win Loss
                </Link>
                )}
                {/* User Bet History — original me downline panel par nahi hai, sirf owner ko */}
                {role === 'owner' && (
                <Link to="/admin/reports/userbethistory" className="side-nav-link-ref dropdown-item">
                  User Bet History
                </Link>
                )}
                {/* User Register Detail — original me downline panel par User Bet History ki jagah ye hai */}
                {can('userList') && (
                <Link to="/admin/reports/userregisterdetail" className="side-nav-link-ref dropdown-item">
                  User Register Detail
                </Link>
                )}

              </div>
            </li>
            )}

            {/* Settlement Dropdown - Owner only */}
            {role === 'owner' && (
              <li className={`nav-item dropdown ${settlementOpen ? 'show' : ''}`} onMouseEnter={closeOtherMenuOnHover('settlement')}>
                <a
                  href="#"
                  className="nav-link dropdown-toggle"
                  onClick={(e) => {
                    e.preventDefault()
                    toggleMenu('settlement')
                  }}
                  role="button"
                  aria-expanded={settlementOpen}
                >
                  Settlement <i className="fa fa-caret-down" />
                </a>
                <div className={`dropdown-menu ${settlementOpen ? 'show' : ''}`}>
                  <Link to="/admin/settlement/match" className="dropdown-item">
                    <span>Match Settlement</span>
                  </Link>
                  <Link to="/admin/settlement/fancy" className="dropdown-item">
                    <span>Fancy Settlement</span>
                  </Link>
                  <Link to="/admin/settlement/void-bets" className="dropdown-item">
                    <span>Void Bets</span>
                  </Link>
                  <Link to="/admin/settlement/total-bets" className="dropdown-item">
                    <span>Total Bets</span>
                  </Link>
                  <Link to="/admin/settlement/casino" className="dropdown-item">
                    <span>Casino Settlement</span>
                  </Link>
                </div>
              </li>
            )}

            {can('loginUserCreation') && (
            <li className="nav-item">
              <Link to="/admin/createaccount" className="nav-link">
                Multi Login
              </Link>
            </li>
            )}
          </ul>
        </div>

        {/* User Menu Section */}
        <div className="d-flex user-menu align-items-center">
          {/* System Tracker integrity bell — hidden. Wapas dikhana ho to niche wali line uncomment karo. */}
          {/* {role === 'owner' && <IntegrityAlertsBell />} */}
          {/* Desktop Balance */}
          {/* <div className="d-none d-sm-flex align-items-center mr-3">
             <span className="text-white font-weight-bold" style={{ fontSize: '14px' }}>
                Pts: {user?.wallet?.inr_balance || 0}
             </span>
          </div> */}

          {/* User Dropdown */}
          <div className="dropdown b-dropdown btn-group" onMouseEnter={closeOtherMenuOnHover('user')}>
            <button
              type="button"
              className="btn dropdown-toggle btn-black header-item"
              onClick={() => toggleMenu('user')}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
            >
              <span className="ml-1 font-[500]">{user?.username || 'User'}</span>

            </button>
            <ul
              className={`dropdown-menu ${userMenuOpen ? 'show' : ''}`}
              role="menu"
            >
              <div className="dropdown d-sm-none ml-1 mr-1">
                <div className="bal-box">
                  {/* <span className="balance nowrap">
                    Pts: <span className="balance-value"><b>{user?.wallet?.inr_balance || 0}</b></span>
                  </span> */}
                </div>
              </div>
              <a href="javascript: void(0);" className="dropdown-item d-sm-none">
                <i className="fas fa-info-circle mr-1"></i> Rules
              </a>
              <Link to="/admin/security-auth" className="dropdown-item">
                Secure Auth Verification
              </Link>
              {role === 'owner' && (
                <a
                  href="javascript:void(0)"
                  className="dropdown-item"
                  onClick={() => {
                    setOpenMenu(null)
                    setDepositModalOpen(true)
                  }}
                >
                  Deposit Balance
                </a>
              )}
              <Link to="/admin/change-password" className="dropdown-item">
                Change Password
              </Link>
              <a
                href="javascript:void(0)"
                className="dropdown-item"
                onClick={handleLogout}
              >
                Logout
              </a>
            </ul>
          </div>

          {/* Search Box */}
          {/* Reference (vue-multiselect): .site-searchbox.mt-3 > .multiselect + " " + .search-icon */}
          <div className="site-searchbox mt-3" ref={searchRef}>
            <div
              className={`multiselect${searchOpen ? ' multiselect--active' : ''}`}
              tabIndex="-1"
              role="combobox"
              aria-owns="listbox-null"
            >
              <div className="multiselect__select"></div>{'  '}
              <div className="multiselect__tags" onClick={handleSearchClick}>
                <div className="multiselect__tags-wrap" style={{ display: 'none' }}></div>{' '}
                <div className="multiselect__spinner" style={{ display: 'none' }}></div>{' '}
                <input
                  name=""
                  type="text"
                  autoComplete="off"
                  spellCheck="false"
                  placeholder="All Client"
                  tabIndex="0"
                  aria-controls="listbox-null"
                  className="multiselect__input"
                  value={searchValue}
                  onChange={handleSearchChange}
                  onClick={handleSearchClick}
                  style={searchOpen ? { width: '100%' } : { width: '0px', position: 'absolute', padding: '0px' }}
                />{' '}
                {!searchOpen && <span className="multiselect__placeholder">All Client</span>}
              </div>{' '}
              <div
                tabIndex="-1"
                className="multiselect__content-wrapper"
                style={searchOpen ? { maxHeight: '300px' } : { maxHeight: '300px', display: 'none' }}
              >
                <ul role="listbox" id="listbox-null" className="multiselect__content" style={{ display: 'block' }}>
                  {' '}
                  {searchResults &&
                    searchResults.map((item) => (
                      <li key={item.user_id || item.wallet_id || item.username}>
                        <span
                          className="multiselect__option"
                          onClick={() => {
                            // Open user modal with full data
                            setSelectedUser(item)
                            setUserModalOpen(true)
                            setSearchValue(item.username || '')
                            setSearchOpen(false)
                            setSearchResults([])
                          }}
                        >
                          <span>{item.username}</span>
                        </span>
                      </li>
                    ))}{' '}
                  {/* vue-multiselect noResult (search with no match) / noOptions (nothing to list) slots */}
                  <li style={searchValue && !searchResults?.length ? undefined : { display: 'none' }}>
                    <span className="multiselect__option">
                      <span>No elements found</span>
                    </span>
                  </li>{' '}
                  <li style={!searchValue && !searchResults?.length ? undefined : { display: 'none' }}>
                    <span className="multiselect__option">List is empty.</span>
                  </li>{' '}
                </ul>
              </div>
            </div>{' '}
            <div className="search-icon" onClick={handleSearchClick}>
              <i className="fas fa-search-plus" />
            </div>
          </div>
        </div>
      {/* User Detail Modal */}
      {userModalOpen && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setUserModalOpen(false)}
        />
      )}

      {/* Owner Self Deposit — dropdown ka "Deposit Balance" (owner only) */}
      {role === 'owner' && (
        <OwnerDepositModal
          show={depositModalOpen}
          onHide={() => setDepositModalOpen(false)}
          onSuccess={refreshOwnerProfile}
        />
      )}



      </div>
    </header>
  )
}

export default Header